/**
 * Werkzeug-Ergebnisse haben eine Obergrenze. (Reformation 04.09., Schritt 3)
 *
 * ## Der Befund
 *
 * `chat_messages.metadata` trug am 04.09. **31 MB** rohe Werkzeug-Ergebnisse
 * bei 3,8 MB echtem Gesprächstext. Die größte einzelne Zeile: **3,93 MB** —
 * eine vollständige Liste offener Browser-Tabs. Die zweitgrößte: 3,3 MB
 * Rohantwort einer Gedächtnissuche.
 *
 * ## Warum das niemandem nützt
 *
 * Niemand hat je mehr als einen Bruchteil davon gesehen:
 *   - Der Verlaufsblock nimmt `roh.slice(0, 400)` — 400 Zeichen.
 *   - Die Karte im Chat rendert je Werkzeug eine Handvoll Treffer.
 *
 * Der Rest lag nur da, wurde bei jeder Verlaufsabfrage mitgelesen und machte
 * die Datenbank um ein Viertel größer.
 *
 * ## Was hier passiert
 *
 * Ein zu großes Ergebnis wird durch seinen ANFANG ersetzt, mit einem
 * ehrlichen Vermerk, wie viel fehlt. Nicht gelöscht, nicht verfälscht — die
 * Karte zeigt weiter, was sie zeigen würde, und wer nachsieht, erfährt, dass
 * gekürzt wurde.
 *
 * ⚠️ Der Anfang eines JSON-Ergebnisses ist selbst kein JSON mehr. Eine Karte,
 *    die ihn lesen will, holt ihn mit gekappterAnfang() — Schreiber und Leser
 *    dieser Form wohnen hier zusammen. (28.09.2026, Ticket 2747)
 */

/**
 * Ab hier wird gekürzt. 16.000 Zeichen sind mehr, als jede Karte je zeigt
 * (die größte Anzeige sind sechs Suchtreffer) und das Vierzigfache dessen,
 * was der Verlaufsblock übernimmt.
 */
export const ERGEBNIS_GRENZE = 16_000;

/** Ein Werkzeug-Ergebnis, wie es in `metadata.toolCards[].result` liegt. */
interface Karte {
  tool?: string;
  result?: unknown;
  [k: string]: unknown;
}

/**
 * Kürzt ein einzelnes Ergebnis, wenn es die Grenze reißt. Gibt es unverändert
 * zurück, wenn es passt — der Normalfall.
 */
export function kappeErgebnis(result: unknown): unknown {
  if (result === null || result === undefined) return result;
  const roh = typeof result === "string" ? result : sicherSerialisiert(result);
  if (roh === null || roh.length <= ERGEBNIS_GRENZE) return result;
  return {
    gekuerzt: true,
    zeichen: roh.length,
    vorschau: roh.slice(0, ERGEBNIS_GRENZE),
    hinweis: `Ergebnis war ${roh.length} Zeichen lang und wurde auf ${ERGEBNIS_GRENZE} gekürzt.`,
  };
}

/** JSON.stringify, das an Zyklen und Sonderwerten nicht zerbricht. */
function sicherSerialisiert(wert: unknown): string | null {
  try {
    return JSON.stringify(wert) ?? null;
  } catch {
    return null;
  }
}

/**
 * Wortlaut, der nicht in den Verlauf gehört. (11.10.2026, Ticket 2878)
 *
 * `alltag_lesen`, `alltag_detail` und (seit der Löschzusage) `alltag_search` geben den Wortlaut einer Tagesaufnahme zurück — Telefonate, Gespräche Dritter, Sehr
 * Privates. Jede Karte landet mit ihrer Antwort DAUERHAFT in chat_messages.metadata: ein Gespräch von 300.000 Zeichen legte
 * 51 Karten an, also eine vollständige zweite Kopie im Verlauf — und löscht man die Aufnahme, bliebe sie stehen (weg nur über
 * „Archiv leeren“ oder die Kontolöschung). Gebraucht wird der Text dort nicht: der Faden zeigt von einer Karte nur Kopf und
 * Rahmen, karten_lesen schließt diese Karten aus (KARTEN_NICHT_LESBAR), und die Aufnahme selbst bleibt die einzige Ablage.
 *
 * Darum wird hier, an der EINEN Stelle, an der Karten liegen bleiben (SqliteStore.appendMessage), der Wortlaut durch einen
 * Platzhalter ersetzt; der Kopf (Kennung, ab/bis/weiter_ab, Länge, Zeit) bleibt. Pro Werkzeug: die Felder, die Wortlaut tragen.
 *
 * ⚠️ Das ist eine Liste der Felder MIT Wortlaut (nicht der Felder, die bleiben dürfen): kommt ein Feld mit Text dazu, muss es hier
 *    stehen. tests/server/alltag-wortlaut-2878.test.ts füllt jedes Feld mit Wortlaut und prüft, dass nichts davon in der Karte bleibt.
 * ⚠️ Eine Karte dieser Werkzeuge, deren Ergebnis weder Objekt noch lesbares JSON ist, verliert das ganze Ergebnis: im Zweifel
 *    lieber kein Text als ein Wortlaut in unbekannter Form.
 * ⚠️ Die Sprecher-Fassung (`alltag_lesen` mit `fassung=sprecher`, Ticket 2878 Teil 2) liefert Wortmeldungen UND Namen der Stimmen im
 *    SELBEN Feld `text` — dasselbe Telefonat Dritter, nur mit Zeiten. Ihr Kopf trägt nur Zahlen (stimmen, wortmeldungen,
 *    unsicher_prozent); der Satz „keine Sprecher-Fassung“ ist fester Servertext. Bekäme das Ergebnis je ein weiteres Feld mit Text
 *    aus der Aufnahme, gehört es hier in `oben`.
 */
export const WORTLAUT_PLATZHALTER = "[Wortlaut nicht im Verlauf gespeichert — die Aufnahme bleibt die einzige Ablage; erneut lesen mit alltag_lesen]";

// Exportiert, damit der Wächter in tests/server/alltag-wortlaut-2878.test.ts seine Werkzeugliste AUS DER TABELLE ableitet: ein neuer Eintrag
// muss auch in KARTEN_NICHT_LESBAR (memory/karten-inhalt.ts) stehen, sonst bietet der Faden „ganz lesen“ für eine Karte mit Platzhalter.
export const WORTLAUT_FELDER: Readonly<Record<string, { oben?: readonly string[]; listen?: Readonly<Record<string, readonly string[]>> }>> = {
  alltag_lesen: { oben: ["text"], listen: { aufnahmen: ["anfang"] } },
  alltag_detail: { listen: { entries: ["content"] } },
  // 11.10.2026 (Löschzusage, Entscheidung des Auftraggebers): auch die Suche. Ihre Einträge tragen bei Aufnahmen ohne
  // Zusammenfassung bis zu 1.800 Zeichen ROHtranskript in `content`; eine Karte, die das dauerhaft hält, wäre eine zweite Kopie.
  // Gleiche Form wie alltag_detail, gleiche Behandlung — und das Skript für den Altbestand (memory/karten-kuerzen.ts) nutzt
  // dieselbe Tabelle.
  alltag_search: { listen: { entries: ["content"] } },
};

/** Ersetzt in einem Objekt die genannten Textfelder; gibt dasselbe Objekt zurück, wenn nichts zu ersetzen war. */
function ohneFelder(o: Record<string, unknown>, felder: readonly string[]): Record<string, unknown> {
  let kopie: Record<string, unknown> | null = null;
  for (const f of felder) {
    if (typeof o[f] === "string" && o[f] !== WORTLAUT_PLATZHALTER) {
      kopie ??= { ...o };
      kopie[f] = WORTLAUT_PLATZHALTER;
    }
  }
  return kopie ?? o;
}

/** Das Ergebnis einer Karte ohne Wortlaut — `result` selbst, wenn es nichts zu streichen gab. Wirft nie. */
export function streifeWortlaut(tool: unknown, result: unknown): unknown {
  const regel = typeof tool === "string" && Object.hasOwn(WORTLAUT_FELDER, tool) ? WORTLAUT_FELDER[tool] : undefined;
  if (!regel) return result;
  const alsText = typeof result === "string";
  let wert: unknown = result;
  if (alsText) {
    try {
      wert = JSON.parse(result) as unknown;
    } catch {
      return WORTLAUT_PLATZHALTER;
    }
  }
  if (!wert || typeof wert !== "object" || Array.isArray(wert)) return alsText ? WORTLAUT_PLATZHALTER : result;
  let neu = ohneFelder(wert as Record<string, unknown>, regel.oben ?? []);
  for (const [liste, felder] of Object.entries(regel.listen ?? {})) {
    const eintraege = neu[liste];
    if (!Array.isArray(eintraege)) continue;
    let geaendert = false;
    const gestrichen = eintraege.map((e: unknown) => {
      if (!e || typeof e !== "object" || Array.isArray(e)) return e;
      const ohne = ohneFelder(e as Record<string, unknown>, felder);
      if (ohne !== e) geaendert = true;
      return ohne;
    });
    if (geaendert) neu = { ...neu, [liste]: gestrichen };
  }
  if (neu === wert) return result;
  return alsText ? JSON.stringify(neu) : neu;
}

/**
 * Kürzt alle Werkzeug-Ergebnisse in einem Metadaten-Objekt — und streicht aus den Karten der Alltag-Werkzeuge den Wortlaut
 * (`streifeWortlaut`, Ticket 2878).
 *
 * Rührt nichts anderes an: unbekannte Felder, andere Metadaten und Karten
 * unter der Grenze kommen unverändert zurück. Wirft nie — eine Nachricht darf
 * nicht daran scheitern, dass ihr Beiwerk seltsam aussieht.
 */
export function kappeMetadaten(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null | undefined {
  if (!metadata || typeof metadata !== "object") return metadata;
  const karten = (metadata as { toolCards?: unknown }).toolCards;
  if (!Array.isArray(karten) || karten.length === 0) return metadata;
  let etwasGekuerzt = false;
  const neu = karten.map((k) => {
    if (!k || typeof k !== "object") return k;
    const karte = k as Karte;
    // Erst den Wortlaut streichen, dann kappen: ein gekappter Anfang des Wortlauts wäre selbst noch Wortlaut.
    const ohneWortlaut = streifeWortlaut(karte.tool, karte.result);
    const gekappt = kappeErgebnis(ohneWortlaut);
    if (gekappt === karte.result) return k;
    etwasGekuerzt = true;
    return { ...karte, result: gekappt };
  });
  return etwasGekuerzt ? { ...metadata, toolCards: neu } : metadata;
}

/**
 * Die Felder eines gekappten Eintrags zurückholen — so weit sein Anfang reicht.
 * (28.09.2026, Ticket 2747)
 *
 * Eine lange Mail kam nach dem Neuladen als `{ gekuerzt, vorschau }` an, und
 * `vorschau` war der abgeschnittene JSON-Text: `{"ok":true,…,"body":"Hallo …`.
 * Kopf und fast der ganze Text standen darin, die Karte konnte ihn nur nicht
 * lesen und zeigte „Es gibt dazu nichts anzuzeigen“.
 *
 * Hier wird der Anfang geschlossen. Ein Textfeld der obersten Ebene, das
 * mitten im Wort abreißt, bleibt: es ist genau der Anfang, den die Kappung
 * behalten wollte. Jedes andere angerissene Feld — mitten im Schlüssel, in
 * einer Zahl, nach dem Doppelpunkt, in einer Liste — fällt GANZ weg, zurück
 * bis zum letzten Komma der obersten Ebene. Eine halbe Zahl oder ein halber
 * Anhang-Name wäre eine falsche Angabe, kein Anfang.
 *
 * Gibt `null`, wenn der Eintrag nicht gekappt ist oder sein Anfang kein
 * JSON-Objekt war (reiner Text). Wirft nie.
 */
export function gekappterAnfang(result: unknown): Record<string, unknown> | null {
  if (!result || typeof result !== "object") return null;
  const k = result as { gekuerzt?: unknown; vorschau?: unknown };
  if (k.gekuerzt !== true || typeof k.vorschau !== "string") return null;
  const roh = k.vorschau.trimStart();
  if (!roh.startsWith("{")) return null;

  // Einmal durchgehen: wie tief wir stehen, ob in einer Zeichenkette, und wo
  // das letzte Komma der obersten Ebene war.
  let tiefe = 0;
  let letztesKomma = -1;
  let inText = false;
  let maskiert = false;
  let maskeBei = -1;
  for (let i = 0; i < roh.length; i++) {
    const c = roh[i];
    if (inText) {
      if (maskiert) maskiert = false;
      else if (c === "\\") {
        maskiert = true;
        maskeBei = i;
      } else if (c === '"') inText = false;
      continue;
    }
    if (c === '"') inText = true;
    else if (c === "{" || c === "[") tiefe++;
    else if (c === "}" || c === "]") tiefe--;
    else if (c === "," && tiefe === 1) letztesKomma = i;
  }

  const versuche: string[] = [];
  if (tiefe === 1 && inText) {
    // Eine halbe Maskierung am Ende (ein „\“ allein, „\u00“) ist noch kein Zeichen.
    const halbeMaske = maskiert || (roh[maskeBei + 1] === "u" && roh.length - maskeBei < 6);
    versuche.push((halbeMaske ? roh.slice(0, maskeBei) : roh) + '"}');
  } else if (tiefe === 1 && !/[\d.+-]\s*$/.test(roh)) {
    versuche.push(roh + "}");
  }
  if (letztesKomma > 0) versuche.push(roh.slice(0, letztesKomma) + "}");
  for (const v of versuche) {
    try {
      const wert: unknown = JSON.parse(v);
      if (wert && typeof wert === "object" && !Array.isArray(wert)) return wert as Record<string, unknown>;
    } catch {
      /* nächster Versuch */
    }
  }
  return null;
}
